import {
  Service as GcpService,
  frnToInfo,
  idToFrn as gcpIdToFrn,
} from "@p0security/integrations-gcloud-shared/asset";
import { resourceFromKey } from "@p0security/integrations-kubernetes-shared/assessment";
import { AWS_RESOURCE_ARN } from "@p0security/shared/assessment/constants";
import {
  fallbackIconUrl as awsFallbackIconUrl,
  serviceIconUrl as awsServiceIconUrl,
} from "@p0security/shared/assessment/media/Aws";
import {
  names as gcpNames,
  navIconUrl as gcpNavIconUrl,
} from "@p0security/shared/assessment/media/GoogleCloud";
import { resourceIconUrl as k8sResourceIconUrl } from "@p0security/shared/assessment/media/Kubernetes";
import {
  googleGenericIcon,
  serviceIconUrl as workspaceIconUrl,
} from "@p0security/shared/assessment/media/Workspace";
import { renderResourceType } from "@p0security/shared/assessment/render";
import { Node } from "@p0security/shared/graph/types";
import { integrationRegistry } from "@p0security/shared/integrations/registry";
import { ProviderOrAll } from "@p0security/shared/types/assessment";
import { AssessmentNodes } from "@p0security/shared/types/assessment/data/index";
import { assertNever } from "@p0security/shared/types/index";
import { camelCase, capitalize, kebabCase, startCase } from "lodash";
import pluralize from "pluralize";

export type ResourceInfo = {
  /** The resource service name */
  service: string;
  /** Any potential subservice */
  type?: string;
  /** The full resource locator */
  locator: string;
  /** The individual resource item identifier */
  id: string;
  /** Is not a specific managed service */
  isGeneric: boolean;
  /** Is an entire service */
  isService: boolean;
  /** The service's icon URL */
  url: string;
};

export const resourceLabels = (
  resource: AssessmentNodes["resource"],
  provider: ProviderOrAll,
  shortPath?: boolean
): ResourceInfo => {
  let { service, type } = resource;
  const { id, locator } = resource;
  let url = "";
  let isGeneric = false;
  switch (provider) {
    case "gcp":
      service = service ?? "cloudresourcemanager";
      url = gcpNavIconUrl(service);
      isGeneric = service === GcpService.crm;
      service = gcpNames[service] ?? service;
      break;
    case "aws":
      service = service ? capitalize(service) : "AWS";
      url = awsServiceIconUrl[service] ?? awsFallbackIconUrl;
      isGeneric = service === "AWS" || type === undefined;
      break;
    case "workspace":
      service = startCase(camelCase(service ?? "Workspace"));
      url = workspaceIconUrl[service] ?? googleGenericIcon;
      isGeneric = false;
      break;
    case "k8s":
      service = service ?? "kubernetes";
      url = k8sResourceIconUrl[service] ?? k8sResourceIconUrl.controlplane;
      isGeneric = true;
      break;

    case "azure":
      // The service is the ARM provider namespace, e.g. "Microsoft.Network"
      service = service
        ? startCase(service.replace(/^Microsoft\./, ""))
        : "Azure";
      url = integrationRegistry.azure.logoUrl;
      isGeneric = true;
      break;

    //TODO: Add support for other providers
    case "all":
    case "azure-ad":
    case "entra-id":
    case "okta":
      service = "(unknown)";
      isGeneric = true;
      break;
    case "custom":
      service = service ?? "custom";
      if (!resource.iconUrl) break;
      try {
        // The icon url is specified by the user
        // Therefore, only display it if it is a valid URL with no search params
        const parsedUrl = new URL(resource.iconUrl);
        if (parsedUrl.searchParams.toString() === "") url = resource.iconUrl;
      } catch (err) {}
      break;
    default:
      assertNever(provider);
  }
  type = renderResourceType(type);
  service = service ?? "unknown";

  return {
    service,
    type,
    id,
    locator: shortPath ? id : locator,
    isGeneric,
    isService: type === undefined,
    url,
  };
};

export const awsResourceInfo = (
  node: Node<AssessmentNodes, "grant">,
  index: number,
  _shortPath?: boolean
): ResourceInfo => {
  const resource = node.data.resources[index];
  const match = resource.match(AWS_RESOURCE_ARN);
  if (match) {
    const [, service, , first, name] = match;
    const id =
      name === "*"
        ? first
          ? `All ${pluralize(first?.replace(/-/g, " "))}`
          : service === "*"
            ? resource
            : `All ${service} resources`
        : name;
    return {
      service,
      type: first ? first.replace(/-/g, " ") : undefined,
      id,
      locator: resource,
      isGeneric: true,
      isService: !first,
      url: awsServiceIconUrl[service] ?? awsFallbackIconUrl,
    };
  }
  return {
    service: resource === "*" ? "AWS accounts" : "unknown",
    type: undefined,
    id: resource === "*" ? (node.data.parent ?? "(Entire account)") : resource,
    locator: resource,
    isGeneric: true,
    isService: false,
    url: awsFallbackIconUrl,
  };
};

export const gcpResourceInfo = (
  resource: string,
  shortPath?: boolean,
  isRollup?: boolean
) => {
  let data;
  if (isRollup || !resource.startsWith("//")) {
    data = frnToInfo(gcpIdToFrn("project", resource));
  } else {
    data = frnToInfo(resource);
  }

  // GCS doesn't use type indicators, so cluge these back (objects have parents in the locator)
  if (data.service === "storage") {
    data.type = data.type ? "objects" : "buckets";
  }
  const id =
    data.service === GcpService.crm
      ? data.type === "organizations"
        ? "(Entire organization)"
        : (data.id ?? "")
      : (data.id ?? "(Entire service)");
  return resourceLabels(
    {
      ...data,
      locator: resource,
      id,
      type: kebabCase(data.type).replace(/-/g, " "),
    },
    "gcp",
    shortPath
  );
};

const all = (value?: string) => value === "*" || value === undefined;

export const k8sResourceInfo = (resource: string, _shortPath?: boolean) => {
  const k8sResource = resourceFromKey(resource);
  // Fall back to the generic icon if the resource key cannot be parsed
  if (!k8sResource) {
    return {
      service: "Entire cluster",
      type: undefined,
      id: resource,
      locator: resource,
      isGeneric: true,
      isService: false,
      url: k8sResourceIconUrl.controlplane,
    };
  }
  const { apiGroup, resourceType, namespace, name } = k8sResource;
  return {
    service: apiGroup,
    type: undefined,
    id:
      all(resourceType) && all(namespace) && all(name)
        ? "Entire cluster"
        : !all(resourceType) && all(namespace) && all(name)
          ? `All ${resourceType} in entire cluster` // `kind` is already plural
          : all(resourceType) && !all(namespace) && all(name)
            ? `All resources in ${namespace} namespace`
            : all(resourceType) && all(namespace) && !all(name) // must be a non-namespaced cluster-level object - is this even possible with `kind = *`?
              ? name
              : !all(resourceType) && !all(namespace) && all(name)
                ? `All ${resourceType} in ${namespace} namespace` // `resourceType` is already plural
                : !all(resourceType) && all(namespace) && !all(name)
                  ? `${name} ${pluralize.singular(resourceType)}` // must be a non-namespaced cluster-level object
                  : all(resourceType) && !all(namespace) && !all(name) // is this even possible with `resourceType = *`?
                    ? `${name} in ${namespace} namespace`
                    : // otherwise every field is !all
                      `${name} ${pluralize.singular(resourceType)} in ${namespace} namespace`,
    locator: resource,
    isGeneric: true,
    isService: false,
    url: k8sResourceIconUrl[resourceType] ?? k8sResourceIconUrl.controlplane,
  };
};

export const workspaceResourceInfo = (
  resource: string,
  _shortPath?: boolean
): ResourceInfo => {
  const title = startCase(camelCase(resource));
  return {
    id: title,
    isGeneric: false,
    isService: true,
    locator: title,
    service: title,
    url: workspaceIconUrl[resource] ?? googleGenericIcon,
  };
};

export const oktaResourceInfo = (resource: string): ResourceInfo => {
  const title = startCase(camelCase(resource));
  return {
    id: title,
    isGeneric: false,
    isService: true,
    locator: title,
    service: title,
    url: integrationRegistry.okta.logoUrl,
  };
};

export const azureAdResourceInfo = (resource: string) => {
  const parsedResource = resource === "/" ? "Entire directory" : resource;
  return {
    id: parsedResource,
    isGeneric: false,
    isService: false,
    locator: parsedResource,
    service: parsedResource,
    url: integrationRegistry["entra-id"].logoUrl,
    type: "",
  };
};

export const azureResourceInfo = (resource: string) => {
  const parsedResource = resource === "/" ? "Entire tenant" : resource;
  return {
    id: parsedResource,
    isGeneric: false,
    isService: false,
    locator: parsedResource,
    service: parsedResource,
    url: integrationRegistry.azure.logoUrl,
    type: "",
  };
};

export const customResourceInfo = (
  resource: string,
  _shortPath?: boolean
): ResourceInfo => {
  return {
    service: "Custom",
    type: undefined,
    id: resource,
    locator: resource,
    isGeneric: true,
    isService: false,
    url: "",
  };
};
